/* ------------------------------------------------------------------ */
/* FAQ content                                                         */
/* ------------------------------------------------------------------ */

export interface FaqItem {
  q: string;
  a: string;
}

/**
 * Per-experience FAQs, keyed by the experience `id`. Rendered at the bottom
 * of each experience detail page.
 */
export const experienceFaqs: Record<string, FaqItem[]> = {
  'flying-dress': [
    {
      q: 'What is included in the Santorini flying dress photoshoot?',
      a: 'Each Santorini flying dress photoshoot includes pick-up and drop-off, one flying dress per person, a female photographer, a female assistant, posing guidance, caldera and volcano-view spots, high resolution raw photos, professionally edited photos and reels.',
    },
    {
      q: 'Can I choose my flying dress?',
      a: 'Yes. You can choose your flying dress from the Flying Muse collection before your photoshoot. Private dress selection through WhatsApp and optional studio changing are available.',
    },
    {
      q: 'Is the flying dress included in the price?',
      a: 'Yes. One flying dress per person is included in every Flying Dress Photoshoot package.',
    },
    {
      q: 'Can I see the flying dress collection before booking?',
      a: 'Yes. You can view the Flying Muse flying dress collection and choose your preferred dress before your Santorini flying dress photoshoot.',
    },
    {
      q: 'Do I need experience posing?',
      a: 'No. Every session includes posing guidance, so you feel confident, elegant and comfortable throughout the photoshoot.',
    },
    {
      q: 'Where does the flying dress photoshoot take place?',
      a: 'The photoshoot takes place in selected Santorini locations, including Imerovigli or Oia, with caldera and volcano views.',
    },
    {
      q: 'Can I book a flying dress photoshoot in Oia?',
      a: 'Yes. Flying dress photoshoots in Oia are available depending on package, timing and availability.',
    },
    {
      q: 'What is the best time for a Santorini flying dress photoshoot?',
      a: 'Early morning and sunset are the most requested times for a Santorini flying dress photoshoot, offering soft light and elegant island views.',
    },
    {
      q: 'How much does a Santorini flying dress photoshoot cost?',
      a: 'Solo flying dress photoshoots start from €290. Couples packages start from €380 per couple. Group packages start from €190 per person.',
    },
    {
      q: 'Can couples book a flying dress photoshoot?',
      a: 'Yes. Couples can book a private Santorini flying dress photoshoot with one-hour or two-hour options.',
    },
    {
      q: 'Can groups book a flying dress photoshoot?',
      a: 'Yes. Group flying dress photoshoots are available, with one flying dress per person and high resolution raw photos and professionally edited photos per person.',
    },
    {
      q: 'When will I receive my photos?',
      a: 'High resolution raw photos are delivered in 1 day. Professionally edited photos are delivered within 3 days.',
    },
    {
      q: 'Are reels included?',
      a: 'Yes. One-hour packages include 1 reel. Two-hour packages include 2 reels.',
    },
  ],
  casual: [
    {
      q: 'What is a Santorini casual photoshoot?',
      a: 'A Santorini casual photoshoot is a private lifestyle photography experience for couples, families, solo travelers and groups. It is relaxed, elegant and focused on natural memories in beautiful Santorini locations.',
    },
    {
      q: 'What is included in the casual photoshoot?',
      a: 'Each casual photoshoot includes pick-up and drop-off, a professional photographer, posing guidance, caldera and volcano-view spots, refreshments and light bites, high resolution raw photos, professionally edited photos and reels.',
    },
    {
      q: 'What is the difference between a casual photoshoot and a flying dress photoshoot?',
      a: 'A casual photoshoot is natural, relaxed and lifestyle-focused. A flying dress photoshoot is more dramatic, styled and cinematic, with one flying dress per person included.',
    },
    {
      q: 'Is the casual photoshoot suitable for couples?',
      a: 'Yes. The couples casual photoshoot is ideal for romantic vacation memories, anniversaries and elegant lifestyle portraits in Santorini.',
    },
    {
      q: 'Is the casual photoshoot suitable for families?',
      a: 'Yes. Family casual photoshoots are available upon request and are designed to feel natural, easy and timeless.',
    },
    {
      q: 'Can solo travelers book a casual photoshoot?',
      a: 'Yes. Solo casual photoshoots are perfect for personal portraits, travel memories and elegant content creation in Santorini.',
    },
    {
      q: 'Is the casual photoshoot good for Instagram content?',
      a: 'Yes. The casual photoshoot is ideal for elegant travel memories, lifestyle portraits and refined Santorini content.',
    },
    {
      q: 'How much does a Santorini casual photoshoot cost?',
      a: 'Solo casual photoshoots start from €230. Couples packages start from €290 per couple. Group packages start from €145 per person.',
    },
    {
      q: 'Do I need to know how to pose?',
      a: 'No. Posing guidance is included in every package, with simple direction throughout the session.',
    },
    {
      q: 'Where does the casual photoshoot take place?',
      a: 'The photoshoot takes place in selected Santorini spots with caldera and volcano views.',
    },
    {
      q: 'Can I book a Santorini casual photoshoot in Oia or Imerovigli?',
      a: 'Yes. Casual photoshoots can take place in selected Santorini locations, including Oia or Imerovigli, depending on timing and availability.',
    },
    {
      q: 'When will I receive my photos?',
      a: 'High resolution raw photos are delivered in 1 day. Professionally edited photos are delivered within 3 days.',
    },
    {
      q: 'Are reels included in the casual photoshoot?',
      a: 'Yes. One-hour packages include 1 reel. Two-hour packages include 2 reels.',
    },
  ],
  'santorini-tour': [
    {
      q: 'What is a Santorini photoshooting tour?',
      a: 'A Santorini photoshooting tour is a private experience that combines local island discovery with professional photography in scenic and hidden locations.',
    },
    {
      q: 'What locations are included in the Santorini photoshooting tour?',
      a: 'The route includes Santorini Heart, Megalochori, three secret locations, hidden gems of the island and caldera and volcano-view spots.',
    },
    {
      q: 'What is included in the photoshooting tour?',
      a: 'The tour includes a local tour guide, private Santorini route, island history and local insight, pick-up and drop-off, posing guidance, refreshments and light bites, 200 high resolution raw photos, 50 professionally edited photos and 3 reels.',
    },
    {
      q: 'How long is the Santorini photoshooting tour?',
      a: 'The Santorini photoshooting tour lasts 3 hours.',
    },
    {
      q: 'How much does the Santorini photoshooting tour cost?',
      a: 'The tour costs €590 for 2 people and €790 for 3-7 people.',
    },
    {
      q: 'Is the Santorini photoshooting tour private?',
      a: 'Yes. The Santorini photoshooting tour is a private experience.',
    },
    {
      q: 'Is the tour suitable for couples?',
      a: 'Yes. It is ideal for couples who want elegant photos and a more authentic Santorini experience.',
    },
    {
      q: 'Is the tour suitable for groups?',
      a: 'Yes. The Santorini photoshooting tour is available for 3-7 people.',
    },
    {
      q: 'Is the Santorini photoshooting tour good for first-time visitors?',
      a: 'Yes. It is ideal for first-time visitors who want to discover beautiful Santorini locations while enjoying professional photography.',
    },
    {
      q: 'Does the photoshooting tour include Oia?',
      a: 'The route focuses on Santorini Heart, Megalochori, secret locations and caldera-view spots. Oia can be requested depending on availability and the final route.',
    },
    {
      q: 'What should I wear for the Santorini photoshooting tour?',
      a: 'Elegant neutral tones, flowing fabrics and comfortable shoes are recommended for a refined look and easy movement between locations.',
    },
    {
      q: 'Can I add a flying dress to the tour?',
      a: 'Yes. A flying dress can be added upon request.',
    },
    {
      q: 'When will I receive my photos?',
      a: 'High resolution raw photos are delivered in 1 day. Professionally edited photos are delivered within 3 days.',
    },
  ],
  'love-story': [
    {
      q: 'What is Your Love Story?',
      a: 'Your Love Story is a tailor-made Santorini photoshoot for proposals, honeymoons, anniversaries, after-wedding sessions and romantic celebrations.',
    },
    {
      q: 'What is included in Your Love Story?',
      a: 'The experience includes an exclusive caldera or volcano-view location, romantic details, pick-up and drop-off, posing guidance, high resolution raw photos, professionally edited photos and optional flying dress experience.',
    },
    {
      q: 'Do you offer secret proposal photography in Santorini?',
      a: 'Yes. Flying Muse offers private Santorini proposal photography with proposal setup, elegant locations and professional posing guidance.',
    },
    {
      q: 'Can you help plan a surprise proposal in Santorini?',
      a: 'Yes. Flying Muse can help arrange a private Santorini proposal photoshoot with setup details, posing guidance and an exclusive caldera or volcano-view location.',
    },
    {
      q: 'Where is the best place for a proposal photoshoot in Santorini?',
      a: 'The best proposal photoshoot locations are private caldera or volcano-view spots selected for beauty, privacy and atmosphere.',
    },
    {
      q: 'How much does a Santorini proposal photoshoot cost?',
      a: 'Secret proposal photoshoots start from €290.',
    },
    {
      q: 'What is included in the secret proposal photoshoot?',
      a: 'The secret proposal photoshoot includes posing guidance, proposal setup, private photoshoot, exclusive view location, 100 high resolution raw photos and 30 professionally edited photos.',
    },
    {
      q: 'Do you offer honeymoon photoshoots in Santorini?',
      a: 'Yes. Honeymoon photoshoots are available as one-hour, two-hour or three-hour honeymoon photoshoot tour experiences.',
    },
    {
      q: 'Can I book a honeymoon photoshoot in Oia or Imerovigli?',
      a: 'Yes. Honeymoon photoshoots can take place in selected Santorini locations, including Oia or Imerovigli, depending on timing and availability.',
    },
    {
      q: 'How much does a Santorini honeymoon photoshoot cost?',
      a: 'Honeymoon and anniversary photoshoots start from €290 for 1 hour and €460 for 2 hours.',
    },
    {
      q: 'What is the Honeymoon Photoshoot Tour?',
      a: 'The Honeymoon Photoshoot Tour is a private 3-hour romantic tour for two, combining Santorini locations, local island discovery and professional photography.',
    },
    {
      q: 'What is the difference between the honeymoon photoshoot and the honeymoon photoshoot tour?',
      a: 'The honeymoon photoshoot is a shorter romantic session. The honeymoon photoshoot tour is a 3-hour private experience for two, combining local island discovery with professional photography.',
    },
    {
      q: 'How much does the Honeymoon Photoshoot Tour cost?',
      a: 'The Honeymoon Photoshoot Tour costs €590 for 2 people.',
    },
    {
      q: 'What is included in the Honeymoon Photoshoot Tour?',
      a: 'It includes a local tour guide, Santorini Heart, Megalochori, three secret locations, posing guidance, 200 high resolution raw photos, 50 professionally edited photos and 3 reels.',
    },
    {
      q: 'Do you offer anniversary photoshoots?',
      a: 'Yes. Anniversary photoshoots are available for couples who want elegant romantic memories in Santorini.',
    },
    {
      q: 'Do you offer after-wedding photoshoots?',
      a: 'Yes. After-wedding photoshoots are available in one-hour and two-hour packages.',
    },
    {
      q: 'Can I add a flying dress to a romantic photoshoot?',
      a: 'Yes. A flying dress can be added to Your Love Story upon request.',
    },
    {
      q: 'Are flowers and love petals included?',
      a: 'A flower bouquet and love petals are available upon request.',
    },
    {
      q: 'When will I receive my photos?',
      a: 'High resolution raw photos are delivered in 1 day. Professionally edited photos are delivered within 3 days.',
    },
  ],
};

/** Site-wide FAQ groups (locations + booking), shown on the Experiences index. */
export const generalFaqs: { title: string; items: FaqItem[] }[] = [
  {
    title: 'Santorini Photoshoot Locations',
    items: [
      {
        q: 'Where do Santorini photoshoots take place?',
        a: 'Flying Muse photoshoots take place in selected Santorini locations with caldera and volcano views, including Oia, Imerovigli, Megalochori, Santorini Heart and hidden island spots, depending on the selected experience.',
      },
      {
        q: 'Do you offer photoshoots in Oia?',
        a: 'Yes. Oia photoshoots are available upon request, depending on package, timing and availability.',
      },
      {
        q: 'Do you offer photoshoots in Imerovigli?',
        a: 'Yes. Imerovigli is one of the most elegant locations for caldera-view photoshoots in Santorini.',
      },
      {
        q: 'Do you offer photoshoots in Megalochori?',
        a: 'Yes. Megalochori is included in the Santorini Photoshooting Tour and Honeymoon Photoshoot Tour.',
      },
      {
        q: 'Can I choose the photoshoot location?',
        a: 'You can share your preferred location, and Flying Muse will suggest the best option based on your package, timing, light and availability.',
      },
    ],
  },
  {
    title: 'Booking & General',
    items: [
      {
        q: 'How do I book a Flying Muse photoshoot?',
        a: 'You can book your Santorini photoshoot through flyingmuse.gr by choosing your preferred experience, date, number of people and package, or by messaging us directly on WhatsApp.',
      },
      {
        q: 'Are all photoshoots private?',
        a: 'Yes. Flying Muse experiences are private and designed around your booking.',
      },
      {
        q: 'Is pick-up and drop-off included?',
        a: 'Yes. Pick-up and drop-off are included in the photoshoot experiences.',
      },
      {
        q: 'What should I wear for my photoshoot?',
        a: 'For casual, proposal and honeymoon photoshoots, elegant neutral tones photograph beautifully in Santorini. For flying dress photoshoots, one flying dress per person is included.',
      },
      {
        q: 'Can I book a sunrise or sunset photoshoot?',
        a: 'Yes. Sunrise and sunset times can be requested, depending on availability.',
      },
      {
        q: 'What happens if the weather is not suitable?',
        a: 'If weather conditions are not suitable, we will arrange the best available solution for your photoshoot.',
      },
      {
        q: 'Are high resolution raw photos included?',
        a: 'Yes. Every package includes high resolution raw photos.',
      },
      {
        q: 'Are professionally edited photos included?',
        a: 'Yes. Every package includes professionally edited photos.',
      },
      {
        q: 'Can I request extra professionally edited photos?',
        a: 'Yes. Extra professionally edited photos are available upon request.',
      },
      {
        q: 'Can I add extra shooting time?',
        a: 'Yes. Extra shooting time can be added upon request, depending on availability.',
      },
      {
        q: 'Do you offer photoshoots for solo travelers?',
        a: 'Yes. Flying Muse offers private photoshoots for solo travelers, including flying dress, casual and tour experiences.',
      },
      {
        q: 'Do you offer photoshoots for couples?',
        a: 'Yes. Flying Muse offers couples photoshoots, flying dress experiences, honeymoon photoshoots, proposal photography and anniversary sessions.',
      },
      {
        q: 'Do you offer group photoshoots?',
        a: 'Yes. Flying Muse offers group photoshoots, group flying dress experiences and private Santorini photoshooting tours.',
      },
      {
        q: 'Do you offer family photoshoots?',
        a: 'Yes. Family casual photoshoots are available upon request.',
      },
      {
        q: 'Where are you based?',
        a: 'Flying Muse is based in Santorini, Greece.',
      },
    ],
  },
];
